import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, X, Filter, ChevronDown, Headphones } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useTicketState } from '@/hooks/useTicketState';
import { useWorkspace } from '@/context/WorkspaceContext';

import { fetchWorkspaceMembers, WorkspaceMemberWithProfile } from '@/lib/workspaceMembers';
import { projectService } from '@/lib/projectService';
import { TicketStatsCards } from '@/components/tickets/TicketStats';
import { TicketListView } from '@/components/tickets/TicketListView';
import { TicketDetailView } from '@/components/tickets/TicketDetailView';
import { CreateTicketDialog } from '@/components/tickets/CreateTicketDialog';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TicketStatus, TICKET_STATUS_CONFIG, TICKET_PRIORITY_CONFIG, TicketPriority } from '@/components/tickets/types';

interface SupportTicketsProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const SupportTickets: React.FC<SupportTicketsProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {

  const { currentWorkspace, userRole } = useWorkspace();


  // Ticket state
  const { tickets, stats, loading, createTicket, changeStatus, deleteTicket, reload } = useTicketState();

  // UI state
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(searchParams.get('id'));

  useEffect(() => {
    const id = searchParams.get('id');
    if (id !== selectedTicketId) {
      setSelectedTicketId(id);
    }
  }, [searchParams]);

  const handleSelectTicket = (id: string | null) => {
    setSelectedTicketId(id);
    const newParams = new URLSearchParams(searchParams);
    if (id) {
      newParams.set('id', id);
    } else {
      newParams.delete('id');
    }
    setSearchParams(newParams);
  };
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | 'all'>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Support data
  const [members, setMembers] = useState<WorkspaceMemberWithProfile[]>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string; email: string; avatar?: string | null }>>([]);

  useEffect(() => { if (searchOpen) searchInputRef.current?.focus(); }, [searchOpen]);

  const loadSupportData = useCallback(async () => {
    if (!currentWorkspace) return;
    const [mems, clientsData] = await Promise.all([
      fetchWorkspaceMembers(currentWorkspace.id),
      projectService.getWorkspaceClients(currentWorkspace.id).catch(() => []),
    ]);
    setMembers(mems.filter(m => m.role !== 'client' as any));
    setClients((clientsData as any[]).map(c => ({ id: c.id, name: c.name, email: c.email, avatar: c.avatar })));
  }, [currentWorkspace?.id]);

  useEffect(() => { loadSupportData(); }, [loadSupportData]);

  const handleMobileMenuToggle = () => {
    const boardsMenuItem = navigationItems.find(item => item.id === 'boards');
    if (boardsMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('boards', boardsMenuItem.subItems, boardsMenuItem.label);
    }
  };

  const handleCreateTicket = async (data: Parameters<typeof createTicket>[0]) => {
    try {
      setCreating(true);
      await createTicket(data as any);
      toast.success('Ticket created successfully');
      setCreateOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!deleteConfirmId) return;
    try {
      setDeleting(true);
      await deleteTicket(deleteConfirmId);
      toast.success('Ticket deleted');
      setDeleteConfirmId(null);
      if (selectedTicketId === deleteConfirmId) handleSelectTicket(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  // Filtering
  const filteredTickets = tickets.filter(t => {
    const matchSearch = !searchQuery ||
      t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `#${t.ticket_number}`.includes(searchQuery) ||
      t.client?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.assignee?.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    return matchSearch && matchStatus && matchPriority;
  });

  const hasActiveFilters = statusFilter !== 'all' || priorityFilter !== 'all';
  const isTeamMember = userRole !== 'client';

  // ── Shared Filter Dropdown
  const filterDropdown = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 rounded-lg border transition-colors whitespace-nowrap ${
          hasActiveFilters
            ? 'bg-workspace-accent text-white border-transparent'
            : isGlobalDarkMode
              ? 'bg-sidebar border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent'
              : 'bg-card border-border hover:bg-muted text-gray-700'
        }`}>
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">Filter</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover border-border w-52">
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</div>
        <DropdownMenuItem onClick={() => setStatusFilter('all')} className="flex items-center justify-between">
          All Statuses {statusFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
        </DropdownMenuItem>
        {Object.entries(TICKET_STATUS_CONFIG).map(([val, cfg]) => (
          <DropdownMenuItem key={val} onClick={() => setStatusFilter(val as TicketStatus)} className="flex items-center justify-between">
            <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />{cfg.label}</span>
            {statusFilter === val && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-border" />
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Priority</div>
        <DropdownMenuItem onClick={() => setPriorityFilter('all')} className="flex items-center justify-between">
          All Priorities {priorityFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
        </DropdownMenuItem>
        {Object.entries(TICKET_PRIORITY_CONFIG).map(([val, cfg]) => (
          <DropdownMenuItem key={val} onClick={() => setPriorityFilter(val as TicketPriority)} className="flex items-center justify-between">
            <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />{cfg.label}</span>
            {priorityFilter === val && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
          </DropdownMenuItem>
        ))}
        {hasActiveFilters && (
          <>
            <DropdownMenuSeparator className="bg-border" />
            <DropdownMenuItem onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); }} className="text-muted-foreground">
              Clear Filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // ── Detail view
  if (selectedTicketId) {
    return (
      <div className="w-full px-6 md:px-10 py-6">
        <Helmet><title>Ticket Detail | AgencyPlus</title></Helmet>
        <TicketDetailView
          ticketId={selectedTicketId}
          onBack={() => { handleSelectTicket(null); reload(); }}
          isTeamMember={isTeamMember}
          members={members}
          onTicketUpdated={reload}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Tickets | AgencyPlus</title>
        <meta name="description" content="Manage customer support tickets and requests" />
        <link rel="canonical" href="/tickets" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={() => setCreateOpen(true)}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search tickets..."
          showSearch={true}
          showAdd={!isTeamMember}
          isDarkMode={isGlobalDarkMode}
        >
          {filterDropdown}
        </MobileSubMenuHeader>
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Tickets</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {stats ? `${stats.total} total · ${stats.open} open` : 'Manage customer support tickets'}
            </p>
          </div>

          <div className="hidden md:flex items-center gap-2">
            {/* Search */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search tickets..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border focus:ring-1 focus:ring-workspace-accent/30"
                    />
                    <button onClick={() => { setSearchQuery(''); setSearchOpen(false); }} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border hover:bg-muted transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Filter */}
            {filterDropdown}

            {/* New Ticket */}
            {!isTeamMember && (
              <button
                onClick={() => setCreateOpen(true)}
                className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                <span className="font-medium">New Ticket</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        {stats && !loading && (
          <div className="hidden md:block">
            <TicketStatsCards stats={stats} />
          </div>
        )}

        {/* Content */}
        {loading ? (
          null
        ) : filteredTickets.length > 0 ? (
          <TicketListView
            tickets={filteredTickets}
            loading={false}
            onOpenTicket={t => handleSelectTicket(t.id)}
            onChangeStatus={(id, status) => changeStatus(id, status).catch(e => toast.error(e.message))}
            onDeleteTicket={id => setDeleteConfirmId(id)}
            isTeamMember={isTeamMember}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-violet-500/10">
              <Headphones className="w-10 h-10 text-violet-500" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-foreground">
              {searchQuery || hasActiveFilters ? 'No tickets found' : 'No tickets yet'}
            </h2>
            <p className="text-center max-w-md mb-6 text-muted-foreground">
              {searchQuery || hasActiveFilters
                ? 'Try adjusting your search or filters'
                : isTeamMember
                ? 'No tickets have been submitted yet.'
                : 'Create your first support ticket to get started.'}
            </p>
            {!isTeamMember && !searchQuery && !hasActiveFilters && (
              <button
                onClick={() => setCreateOpen(true)}
                className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="font-medium">New Ticket</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <CreateTicketDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreateTicket as any}
        clients={clients}
        members={members}
        submitting={creating}
      />

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Delete Ticket</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This will permanently delete the ticket and all its replies and activity. This action cannot be undone.
          </p>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="h-10">Cancel</Button>
            <Button
              onClick={handleDeleteTicket}
              disabled={deleting}
              className="h-10 bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? 'Deleting...' : 'Delete Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTickets;
